import { useTranslation } from 'react-i18next'
import { Skeleton } from '@heroui/react'
import { useStoreProductDetails } from '@/hooks/use-store-product-details'
import { useAppSelector } from '@/redux/hooks'
import { ProductDetails } from './product-details'
import { DEFAULT_COUNTRY_CODE } from '../helpers/constants'

interface ProductDetailsDrawerProps {
  onClose: () => void
  productId?: string
  returnedItemId: string
}

export const ProductDetailsDrawer = ({
  onClose,
  returnedItemId,
  productId
}: ProductDetailsDrawerProps) => {
  const {
    t,
    i18n: { language }
  } = useTranslation()

  const settings = useAppSelector(
    (store) => store.settingsApi.settings.response
  )

  const currency = useAppSelector(
    (store) => store.returnsApi.order.response?.order?.money_format
  )?.currency

  const currentReturn = useAppSelector((store) => store.return)

  const orderReference = useAppSelector(
    (store) =>
      store.returnsApi.order.response?.order?.identification
        ?.customer_printed_order_id
  )

  const { data, status } = useStoreProductDetails({
    ecommerceId: settings.ecommerce_id,
    language,
    currency,
    countryCode:
      currentReturn.returnAddress.country_code ?? DEFAULT_COUNTRY_CODE,
    productId: productId,
    orderReference
  })

  if (status === 'pending') {
    return (
      <div className="mx-auto flex size-full flex-col gap-4 overflow-auto overscroll-none px-3 pt-9 md:flex-row md:gap-8 md:py-9 2xl:max-w-6xl">
        <div className="flex h-[62vw] w-full gap-2 md:h-auto md:max-h-full md:w-fit md:flex-row-reverse">
          <Skeleton className="size-full max-h-full rounded-md md:h-auto md:w-[26rem]" />
          <div className="flex flex-col gap-2 overflow-auto">
            {[...Array(6)].map((_, i) => (
              <Skeleton
                className="min-h-28 w-28 rounded-md md:min-h-16 md:w-16"
                key={i}
              />
            ))}
          </div>
        </div>
        <div className="flex-1">
          <Skeleton className="h-8 w-72 rounded-md" />
          <div className="pt-1">
            <Skeleton className="min-h-7 w-32 rounded-md" />
          </div>
          <div className="pb-6 pt-5">
            <Skeleton className="min-h-16 w-full rounded-md" />
          </div>
          <div className="space-y-5">
            <Skeleton className="h-5 w-40 rounded-md" />
          </div>
          <div className="flex flex-col gap-2 pt-4">
            {[...Array(4)].map((_, index) => (
              <Skeleton
                className="min-h-[60px] w-full rounded-md"
                key={index}
              />
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className="mx-auto flex h-full items-center justify-center text-sm">
        {t('toast_errors.error_406')}
      </div>
    )
  }

  return (
    <ProductDetails
      product={data}
      returnedItemId={returnedItemId}
      onClose={onClose}
    />
  )
}
